import React, { useMemo } from 'react'
import RenderHTML from '../../ui-components/RenderHTML'
import { FDInvestmentJourneyResponseType, FundTypeEnum } from '../../../types/fd-type'
import { formatIndianNumberWithDecimals, formatTimePeriod } from '../../../utils/FD/fd'

interface Step {
  title: string
  isCompleted: boolean
  details?: string
}

export const Progress = ({
  data,
  currentInvestment
}: {
  data: FDInvestmentJourneyResponseType | undefined
  currentInvestment: number
}) => {
  const steps: Step[] = useMemo(() => {
    const allSteps = [
      {
        title: 'Setup completed',
        isCompleted: true,
        details: `<div style="display: flex; flex-direction: column; gap: 4px;"> 
      <div>${data?.institutionName} </div>  
      <div> ₹${formatIndianNumberWithDecimals(currentInvestment, 2)}・${data?.interestRate}%・${formatTimePeriod((data?.tenure ?? 0) / 365)} </div> 
      </div>`
      },
      { title: 'Confirm plan details', isCompleted: false },
      { title: 'Make payment', isCompleted: false },
      { title: 'Complete KYC', isCompleted: false }
    ]
    return data?.fundType === FundTypeEnum.BANK ? allSteps : allSteps?.filter((step) => step.title !== 'Complete KYC')
  }, [data])

  return (
    <div
      style={{ background: 'rgba(255, 255, 255, 0.03)' }}
      className='flex flex-col p-5 text-white border border-[#33A7E4] backdrop-blur-[27px] rounded-[16px] w-full'
    >
      {steps.map((step, index) => (
        <ProgressStep key={index} step={step} isLast={index === steps.length - 1} />
      ))}
    </div>
  )
}

interface ProgressStepProps {
  step: Step
  isLast: boolean
}

const ProgressStep: React.FC<ProgressStepProps> = ({ step, isLast }) => (
  <div className={`flex gap-4 items-start relative ${!isLast ? 'mb-3' : ''}`}>
    <div className='flex flex-col'>
      <ProgressStepIcon isCompleted={step.isCompleted} />
      <ProgressStepConnector isVisible={!isLast} isCompleted={step.isCompleted} />
    </div>

    <ProgressStepText title={step.title} details={step.details} />
  </div>
)

interface ProgressStepConnectorProps {
  isCompleted: boolean
  isVisible: boolean
}

const ProgressStepConnector: React.FC<ProgressStepConnectorProps> = ({ isVisible, isCompleted }) =>
  isVisible ? (
    <div
      style={{ marginLeft: 'calc(50% - 1px)', height: `${isCompleted ? 66 : 20}px` }}
      className='bg-[#33A7E4] backdrop-blur-[27px] w-[2px] opacity-50'
    ></div>
  ) : null

interface ProgressStepIconProps {
  isCompleted: boolean
}

const ProgressStepIcon: React.FC<ProgressStepIconProps> = ({ isCompleted }) => (
  <div className={`w-5 h-5 flex justify-center items-center mb-3`}>
    {isCompleted ? (
      <div className='bg-[#D1ECFF] rounded-[50%] h-full w-full'>
        <RenderHTML innerHTMLText={CompletedIcon} />
      </div>
    ) : (
      <div className='border-2 border-[#33A7E4] bg-white/3 rounded-[50%] h-full w-full'></div>
    )}
  </div>
)

interface ProgressStepTextProps {
  title: string
  details?: string
}

const ProgressStepText: React.FC<ProgressStepTextProps> = ({ title, details }) => (
  <div className='flex flex-col -mt-[2px]'>
    <div className='font-[400] text-[#D1ECFF] opacity-90'>{title}</div>
    {details && (
      <div className='text-sm opacity-80 text-[#9BD8FF] pt-[7px]'>
        <RenderHTML innerHTMLText={details} />
      </div>
    )}
  </div>
)

const CompletedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M5.5 11L8 13.5L14.5 7" stroke="#1382D1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`
